import ClickableLinkPlugin from './ClickableLinkPlugin';
import { CodeHighlightPlugin } from './CodeHighlightPlugin';
import { MarkdownRendererSyncPlugin } from './MarkdownRendererSyncPlugin';
import StyledDiv from './StyledDiv';
import $populateMarkdown from './populateMarkdown';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { Typography } from '@mui/material';
import { useMemo } from 'react';

type Props = {
  markdown?: string;
  onClick?: (url: string, target: '_blank' | '_self' | '_parent' | '_top' | null) => void;
};

/** Renders a read-only Markdown snapshot with Lexical formatting and optional link handling. */
export const MarkdownRenderer = ({ markdown, onClick }: Props) => {
  const normalizedMarkdown = markdown ?? '';
  const populateMarkdown = normalizedMarkdown ? () => $populateMarkdown(normalizedMarkdown) : undefined;

  const initialConfig = useMemo(() => {
    const initialConfig: InitialConfigType = {
      editable: false,
      namespace: 'MarkdownRenderer',
      theme: {
        root: 'markDownRenderer_root',
        list: {
          ul: 'markDownRenderer_ul',
          ol: 'markDownRenderer_ol',
        },
        code: 'markDownRenderer_code',
        text: {
          strikethrough: 'markDownRenderer_textStrikethrough',
          underlineStrikethrough: 'markDownRenderer_textUnderlineStrikethrough',
          subscript: 'markDownRenderer_textSubscript',
          superscript: 'markDownRenderer_textSuperscript',
        },
      },
      onError: (error) => {
        console.log(error);
      },
      nodes: [
        CodeNode,
        CodeHighlightNode,
        AutoLinkNode,
        HeadingNode,
        HorizontalRuleNode,
        LinkNode,
        ListItemNode,
        ListNode,
        MarkNode,
        OverflowNode,
        QuoteNode,
        TableCellNode,
        TableNode,
        TableRowNode,
      ],
    };

    return initialConfig;
  }, []);

  return (
    <StyledDiv>
      <LexicalComposer initialConfig={{ ...initialConfig, editorState: populateMarkdown }}>
        <RichTextPlugin
          contentEditable={<ContentEditable />}
          placeholder={
            // eslint-disable-next-line no-restricted-syntax
            <Typography color="text.secondary" sx={{ position: 'absolute', top: 0, left: 0, padding: 1 }}>
              Enter some text...
            </Typography>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        {onClick && <ClickableLinkPlugin onClick={onClick} />}
        <CodeHighlightPlugin />
        <MarkdownRendererSyncPlugin markdown={normalizedMarkdown} />
      </LexicalComposer>
    </StyledDiv>
  );
};
